import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError, fmtDate } from "./api";
import { Card, PageHead, Pill, Btn, Input, Textarea, Select, Empty } from "./widgets";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export const LEAD_STATUSES = ["new", "contacted", "consultation scheduled", "qualified", "in progress", "client", "closed", "not a fit"];
const TONE = { new: "champagne", contacted: "blue", client: "green", closed: "gray", "not a fit": "red" };

const Leads = () => {
  const [leads, setLeads] = useState(null);
  const [status, setStatus] = useState("");

  const load = () =>
    api.get(`/admin/leads${status ? `?status=${encodeURIComponent(status)}` : ""}`)
      .then((r) => setLeads(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, [status]);

  const setLeadStatus = async (id, s) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status: s });
      toast.success("Status updated.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="leads-page">
      <PageHead
        title="Leads"
        sub="Every form submission becomes a CRM record here first."
        actions={
          <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leads-filter">
            <option value="">All statuses</option>
            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        }
      />
      {!leads ? (
        <p className="text-sm text-[#171717]/50">Loading…</p>
      ) : leads.length === 0 ? (
        <Empty>No leads yet. New consultation and contact requests appear here automatically.</Empty>
      ) : (
        <>
          {/* Mobile: card list (no wide tables on phones) */}
          <div className="space-y-3 lg:hidden">
            {leads.map((l) => (
              <Card key={l.id} data-testid={`lead-card-${l.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/admin/leads/${l.id}`} className="text-sm text-[#171717] hover:text-[#8a6f3e]">{l.name}</Link>
                    <p className="mt-1 break-all text-xs text-[#171717]/60">{l.email}</p>
                    <p className="text-xs text-[#171717]/60">{l.phone}</p>
                  </div>
                  <Pill tone={TONE[l.status] || "gray"}>{l.status}</Pill>
                </div>
                <p className="mt-3 text-xs text-[#171717]/55">{l.service} · {l.source} · {l.lang}</p>
                <p className="mt-1 text-xs text-[#171717]/45">{fmtDate(l.createdAt)}</p>
                <Select className="mt-3 w-full" value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)} data-testid={`lead-status-mobile-${l.id}`}>
                  {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Card>
            ))}
          </div>

          {/* Desktop: full table */}
          <Card className="hidden overflow-x-auto p-0 lg:block">
            <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-[#171717]/10 text-left text-[0.6rem] uppercase tracking-[0.16em] text-[#171717]/45">
                <th className="px-5 py-3.5 font-medium">Name</th>
                <th className="px-5 py-3.5 font-medium">Contact</th>
                <th className="px-5 py-3.5 font-medium">Service</th>
                <th className="px-5 py-3.5 font-medium">Source</th>
                <th className="px-5 py-3.5 font-medium">Created</th>
                <th className="px-5 py-3.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-[#171717]/5 hover:bg-[#F4F0E8]/60" data-testid={`lead-row-${l.id}`}>
                  <td className="px-5 py-3.5">
                    <Link to={`/admin/leads/${l.id}`} className="text-[#171717] hover:text-[#8a6f3e]">{l.name}</Link>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-[#171717]/60">{l.email}<br />{l.phone}</td>
                  <td className="px-5 py-3.5 text-xs text-[#171717]/60">{l.service}</td>
                  <td className="px-5 py-3.5 text-xs text-[#171717]/60">{l.source} · {l.lang}</td>
                  <td className="px-5 py-3.5 text-xs text-[#171717]/55">{fmtDate(l.createdAt)}</td>
                  <td className="px-5 py-3.5">
                    <Select value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`}>
                      {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        </>
      )}
    </div>
  );
};

export const LeadProfile = () => {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get(`/admin/leads/${id}`).then((r) => setLead(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, [id]);

  if (!lead) return <p className="text-sm text-[#171717]/50">Loading…</p>;

  const patch = async (payload) => {
    try {
      await api.patch(`/admin/leads/${id}`, payload);
      setNote("");
      load();
      toast.success("Saved.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="lead-profile">
      <PageHead
        title={lead.name}
        sub={`${lead.service} · ${lead.lang === "es" ? "Español" : "English"} · ${fmtDate(lead.createdAt)}`}
        actions={<Link to="/admin/leads" className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.16em] text-[#8a6f3e] hover:underline"><ArrowLeft className="h-3.5 w-3.5" /> All leads</Link>}
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Card>
            <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Contact information</p>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-[#171717]/50">Email</dt><dd>{lead.email}</dd></div>
              <div className="flex justify-between"><dt className="text-[#171717]/50">Phone</dt><dd>{lead.phone}</dd></div>
              <div className="flex justify-between"><dt className="text-[#171717]/50">Preferred</dt><dd>{lead.preferredContact}</dd></div>
              <div className="flex justify-between"><dt className="text-[#171717]/50">Consultation</dt><dd>{lead.consultationType}</dd></div>
              <div className="flex justify-between"><dt className="text-[#171717]/50">Source</dt><dd>{lead.source}</dd></div>
              <div className="flex justify-between"><dt className="text-[#171717]/50">Landing page</dt><dd>{lead.landingPage}</dd></div>
            </dl>
            {lead.message && <p className="mt-4 border-t border-[#171717]/10 pt-4 text-sm leading-relaxed text-[#171717]/70">“{lead.message}”</p>}
          </Card>
          <Card>
            <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Status & assignment</p>
            <div className="flex flex-wrap gap-3">
              <Select value={lead.status} onChange={(e) => patch({ status: e.target.value })} data-testid="lead-profile-status">
                {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
              <Input
                placeholder="Assigned to…"
                defaultValue={lead.assignedTo}
                onBlur={(e) => e.target.value !== lead.assignedTo && patch({ assignedTo: e.target.value })}
                className="max-w-52"
              />
            </div>
          </Card>
          <Card>
            <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Internal notes</p>
            <div className="space-y-3">
              {(lead.notes || []).map((n) => (
                <div key={n.id} className="border-l-2 border-[#C6A46A] pl-3 text-sm text-[#171717]/75">
                  {n.text}
                  <p className="mt-0.5 text-[0.62rem] text-[#171717]/40">{n.by} · {fmtDate(n.ts)}</p>
                </div>
              ))}
              {(!lead.notes || lead.notes.length === 0) && <p className="text-xs text-[#171717]/45">No notes yet.</p>}
            </div>
            <div className="mt-4 flex gap-2">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="lead-note" />
              <Btn primary onClick={() => note.trim() && patch({ note })} data-testid="lead-note-add">Add</Btn>
            </div>
          </Card>
        </div>
        <Card className="h-fit">
          <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Timeline</p>
          <div className="space-y-4">
            {(lead.timeline || []).slice().reverse().map((t, i) => (
              <div key={i} className="flex gap-4">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#C6A46A]" />
                <div>
                  <p className="text-sm text-[#171717]/80">{t.text}</p>
                  <p className="text-[0.62rem] text-[#171717]/40">{fmtDate(t.ts)}{t.by ? ` · ${t.by}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Mobile sticky contact actions — above the admin bottom nav */}
      <div className="fixed inset-x-0 bottom-[calc(61px+env(safe-area-inset-bottom))] z-30 grid grid-cols-2 gap-2 border-t border-[#171717]/10 bg-[#F4F0E8]/95 p-3 backdrop-blur-md lg:hidden" data-testid="lead-mobile-actions">
        <a
          href={`tel:+1${String(lead.phone || "").replace(/\D/g, "")}`}
          data-testid="lead-call"
          className="inline-flex min-h-[44px] items-center justify-center rounded-[3px] border border-[#171717]/20 text-[0.65rem] font-medium uppercase tracking-[0.16em] text-[#171717]/75"
        >
          Call
        </a>
        <a
          href={`mailto:${lead.email}`}
          data-testid="lead-email"
          className="inline-flex min-h-[44px] items-center justify-center rounded-[3px] border border-[#C6A46A] bg-[#C6A46A] text-[0.65rem] font-medium uppercase tracking-[0.16em] text-[#171717]"
        >
          Email
        </a>
      </div>
    </div>
  );
};

export default Leads;
